import { getCachedPack, setCachedPack } from './cache';
import { verifyBundleSignature } from './hmac';
import type { PackBundle, PackQuestion } from './types';

export interface LoadPackOpts {
  apiBase: string;
  roomId:  string;
  /** Spectator JWT (?token=…). */
  token:   string;
  /** Optional PACK_SIGN_KEY — if absent, the bundle is loaded without
   *  HMAC verification and a console warning is logged. */
  signKey?: string;
  /** Allow injecting a custom fetcher for tests. */
  fetcher?: typeof fetch;
}

// Orchestrates the spectator pack fetch:
//   1. GET /rooms/:id/pack-url?token=…  → { packId, url }
//   2. fetch the signed R2 URL → PackBundle JSON
//   3. (optional) HMAC-verify with PACK_SIGN_KEY
//   4. cache in localStorage
//
// Returns the bundle; throws on transport or verification failure.
export async function loadPack(opts: LoadPackOpts): Promise<PackBundle> {
  const f = opts.fetcher ?? fetch;

  const meta = await f(
    `${opts.apiBase}/rooms/${opts.roomId}/pack-url?token=${encodeURIComponent(opts.token)}`,
    { method: 'GET' },
  );
  if (!meta.ok) throw new Error(`pack-url: HTTP ${meta.status}`);
  const { packId, url } = (await meta.json()) as { packId: string; url: string };

  const cached = getCachedPack(packId);
  if (cached) return cached;

  const bundleRes = await f(url, { method: 'GET' });
  if (!bundleRes.ok) throw new Error(`pack bundle: HTTP ${bundleRes.status}`);
  const bundle = (await bundleRes.json()) as PackBundle;

  if (opts.signKey) {
    const ok = await verifyBundleSignature(bundle, opts.signKey);
    if (!ok) throw new Error(`pack ${packId} signature mismatch`);
  } else {
    console.warn(`pack ${packId} loaded without HMAC verification (no PACK_SIGN_KEY in bootstrap)`);
  }

  setCachedPack(bundle);
  return bundle;
}

// O(1) question lookup; constructed lazily on the consumer side.
export function indexQuestions(bundle: PackBundle): Map<string, PackQuestion> {
  const m = new Map<string, PackQuestion>();
  for (const q of bundle.questions) m.set(q.id, q);
  return m;
}
